"use client";

import { useState } from "react";
import Flex from "~/components/flex/Flex";
import Text from "~/components/texts/Text";
import { Icon, IconName } from "~/components/icons/Icon";
import { colors, spacing } from "~/utils/tailwindUtils";
import { twMerge } from "tailwind-merge";

type CollapsibleSectionProps = {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  className?: string;
  headerClassName?: string;
  contentClassName?: string;
};

/**
 * CollapsibleSection component that allows expanding/collapsing content
 * @param title - The title text displayed in the header
 * @param children - The content to be shown when expanded
 * @param defaultExpanded - Whether the section should be expanded by default (default: true)
 * @param className - Additional classes for the container
 * @param headerClassName - Additional classes for the header
 * @param contentClassName - Additional classes for the content area
 */
const CollapsibleSection = ({
  title,
  children,
  defaultExpanded = true,
  className,
  headerClassName,
  contentClassName,
}: CollapsibleSectionProps) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const toggleExpanded = () => {
    setIsExpanded((prev) => !prev);
  };

  return (
    <Flex className={twMerge("flex-col", className)}>
      <Flex
        className={twMerge(
          "items-center justify-between cursor-pointer hover:opacity-80 transition-opacity",
          headerClassName
        )}
        onClick={toggleExpanded}
      >
        <Text variant="h3" fontWeight="semibold" color="black">
          {title}
        </Text>
        <Icon
          name={isExpanded ? IconName.ChevronUp : IconName.ChevronDown}
          size={spacing[5]}
          color={colors.gray[600]}
        />
      </Flex>
      {isExpanded && (
        <Flex className={twMerge("flex-col", contentClassName)}>{children}</Flex>
      )}
    </Flex>
  );
};

export default CollapsibleSection;
